/**
 * R2 - React Context provider for Sanity CMS data
 * R4 - Single fetch on app mount, shared across all pages
 * 
 * Wraps the app so any component can access landing page data
 * without re-fetching. Falls back gracefully when Sanity is unavailable.
 */

import { createContext, useContext } from 'react';
import { useSanityData } from './useSanityData';
import type { LandingPageData } from './types';

interface SanityContextValue {
  data: LandingPageData | null;
  isLoading: boolean;
  error: string | null;
}

const SanityContext = createContext<SanityContextValue>({
  data: null,
  isLoading: true,
  error: null,
});

export function SanityDataProvider({ children }: { children: React.ReactNode }) {
  const state = useSanityData();

  return (
    <SanityContext.Provider value={state}>
      {children}
    </SanityContext.Provider>
  );
}

/**
 * R2 - Hook to consume Sanity data from any component
 * @example
 * const { data } = useSanityContext();
 * const title = data?.hero?.badge ?? 'Fallback text';
 */
export function useSanityContext(): SanityContextValue {
  return useContext(SanityContext);
}
